import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function Method() {
  const { t } = useTranslation();
  const timelineRef = useRef<HTMLDivElement>(null);
  const isTimelineInView = useInView(timelineRef, { once: true, margin: '-100px 0px' });

  const phases = [
    {
      num: '01',
      title: t('phoenix.phase1Title'),
      time: t('phoenix.phase1Time'),
      items: [
        t('phoenix.phase1Item1'),
        t('phoenix.phase1Item2'),
        t('phoenix.phase1Item3'),
        t('phoenix.phase1Item4'),
        t('phoenix.phase1Item5'),
      ],
      desc: t('phoenix.phase1Desc'),
      bgClass: 'bg-background-50',
      accentClass: 'from-primary-500 to-primary-400',
    },
    {
      num: '02',
      title: t('phoenix.phase2Title'),
      time: t('phoenix.phase2Time'),
      items: [
        t('phoenix.phase2Item1'),
        t('phoenix.phase2Item2'),
        t('phoenix.phase2Item3'),
        t('phoenix.phase2Item4'),
        t('phoenix.phase2Item5'),
        t('phoenix.phase2Item6'),
      ],
      desc: t('phoenix.phase2Desc'),
      bgClass: 'bg-background-50',
      accentClass: 'from-accent-500 to-accent-400',
    },
    {
      num: '03',
      title: t('phoenix.phase3Title'),
      time: t('phoenix.phase3Time'),
      items: [
        t('phoenix.phase3Item1'),
        t('phoenix.phase3Item2'),
        t('phoenix.phase3Item3'),
        t('phoenix.phase3Item4'),
        t('phoenix.phase3Item5'),
        t('phoenix.phase3Item6'),
        t('phoenix.phase3Item7'),
      ],
      desc: t('phoenix.phase3Desc'),
      bgClass: 'bg-background-50',
      accentClass: 'from-primary-500 to-primary-400',
    },
  ];

  return (
    <section id="method" className="relative py-20 md:py-28 bg-white">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <ScrollReveal direction="up">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-50 border border-primary-200/50 text-primary-700 text-xs font-semibold tracking-wide uppercase mb-4">
            {t('phoenix.methodTag')}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-16">
            Tres fases. <span className="text-primary-500">Un resultado.</span>
          </h2>
        </ScrollReveal>

        <div ref={timelineRef} className="relative">
          <motion.div
            className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-background-300 origin-top"
            style={{ transformOrigin: 'top' }}
            animate={isTimelineInView ? { scaleY: 1 } : { scaleY: 0 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          ></motion.div>

          <div className="flex flex-col gap-12 md:gap-20">
            {phases.map((phase, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px 0px' }}
                transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                className={`relative flex flex-col md:flex-row items-start gap-6 md:gap-0 ${i % 2 === 0 ? '' : 'md:flex-row-reverse'}`}
              >
                <div className={`flex-1 ${i % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16'}`}>
                  <div className={`flex items-center gap-3 mb-3 ${i % 2 === 0 ? 'md:justify-end' : ''}`}>
                    <span className={`font-heading text-4xl md:text-5xl font-bold bg-gradient-to-r ${phase.accentClass} bg-clip-text text-transparent`}>
                      {phase.num}
                    </span>
                    <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                      {phase.time}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">{phase.title}</h3>
                  <ul className={`space-y-1.5 mb-4 ${i % 2 === 0 ? 'md:flex md:flex-col md:items-end' : ''}`}>
                    {phase.items.map((item, j) => (
                      <li key={j} className="flex items-center gap-2 text-foreground-600 text-sm">
                        {i % 2 === 0 ? (
                          <>
                            {item}
                            <span className="w-1.5 h-1.5 rounded-full bg-background-400 shrink-0"></span>
                          </>
                        ) : (
                          <>
                            <span className="w-1.5 h-1.5 rounded-full bg-background-400 shrink-0"></span>
                            {item}
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                  <p className={`text-foreground-500 text-sm leading-relaxed max-w-[400px] ${i % 2 === 0 ? 'md:ml-auto' : ''}`}>
                    {phase.desc}
                  </p>
                </div>

                <div className="relative flex items-center justify-center shrink-0 z-10">
                  <div className="w-12 h-12 rounded-full bg-white border-2 border-primary-500/20 flex items-center justify-center shadow-[0_0_0_6px_rgba(229,57,53,0.06)]">
                    <div className="w-3 h-3 rounded-full bg-primary-500"></div>
                  </div>
                </div>

                <div className="flex-1 hidden md:block"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}